/**
 * MA-ARCH-131: Impulse Alignment Analytics Card
 * MA-ARCH-136A: UI Polish - Decision Clarity Upgrade
 * 
 * Displays metrics comparing performance when scripts reinforce the original
 * acquisition messaging (aligned) vs when they don't (misaligned).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { AlertTriangle, CheckCircle2, TrendingUp, TrendingDown, Minus, Zap, Target, DollarSign, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '@/lib/api';
import { WarningBanner, HELPER_DEFINITIONS } from './HelperText';

function useImpulseAlignment({ period, campaignId, profitPathId, workspaceId, channel, vertical, tenantId }) {
  return useQuery({
    queryKey: ['impulse-alignment', period, campaignId, profitPathId, workspaceId, channel, vertical, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (profitPathId && profitPathId !== 'all') params.append('profit_path_id', profitPathId);
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const response = await api.get(`/analytics/impulse-alignment?${params}`);
      return response.data;
    },
    staleTime: 30000,
  });
}

function DeltaIndicator({ value, suffix = '', higherIsBetter = true, size = 'default' }) {
  const isPositive = value > 0;
  const isNeutral = value === 0;
  
  const status = isNeutral ? 'neutral' : (isPositive === higherIsBetter ? 'good' : 'bad');
  
  const Icon = isNeutral ? Minus : (isPositive ? TrendingUp : TrendingDown);
  
  const sizeClasses = {
    small: 'text-xs',
    default: 'text-sm',
    large: 'text-base font-medium',
  };
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1',
      sizeClasses[size],
      status === 'good' && 'text-emerald-400',
      status === 'bad' && 'text-red-400',
      status === 'neutral' && 'text-slate-400',
    )}>
      <Icon className="h-3 w-3" />
      {isPositive ? '+' : ''}{value.toFixed(1)}{suffix}
    </span>
  );
}

function MetricCard({ label, aligned, misaligned, delta, suffix = '', higherIsBetter = true, icon: Icon }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-xs text-emerald-400/80 mb-0.5">Aligned</div>
          <div className="text-lg font-semibold text-white">{aligned.toFixed(1)}{suffix}</div>
        </div>
        <div>
          <div className="text-xs text-amber-400/80 mb-0.5">Misaligned</div>
          <div className="text-lg font-semibold text-white">{misaligned.toFixed(1)}{suffix}</div>
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-0.5">Delta</div>
          <DeltaIndicator value={delta} suffix={suffix} higherIsBetter={higherIsBetter} size="large" />
        </div>
      </div>
    </div>
  );
}

export default function ImpulseAlignmentCard({
  period = 'mtd',
  campaignId,
  profitPathId,
  workspaceId,
  channel,
  vertical,
  tenantId,
  compact = false,
}) {
  const { data, isLoading, error } = useImpulseAlignment({
    period,
    campaignId,
    profitPathId,
    workspaceId,
    channel,
    vertical,
    tenantId,
  });

  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-violet-400" />
            Impulse Alignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-20 bg-slate-800 rounded" />
            <div className="h-16 bg-slate-800 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-slate-900/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-violet-400" />
            Impulse Alignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400">Unable to load alignment data</p>
        </CardContent>
      </Card>
    );
  }

  const { metrics, inconsistency_detected, inconsistency_rate, warning_message } = data;
  const hasData = metrics.total_eligible_appointments > 0;

  if (compact) {
    return (
      <Card className={cn(
        "bg-slate-900/50 border-slate-700/50",
        inconsistency_detected && "border-amber-500/50"
      )}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-violet-400" />
              <span className="font-medium text-white">Impulse Alignment</span>
            </div>
            {hasData && (
              <div className={cn(
                "px-2 py-1 rounded text-xs font-medium",
                metrics.alignment_rate >= 70 ? "bg-emerald-500/20 text-emerald-400" :
                metrics.alignment_rate >= 50 ? "bg-amber-500/20 text-amber-400" :
                "bg-red-500/20 text-red-400"
              )}>
                {metrics.alignment_rate.toFixed(0)}% aligned
              </div>
            )}
          </div>
          
          {!hasData ? (
            <p className="text-sm text-slate-400">No eligible appointments with script + message variant data</p>
          ) : (
            <>
              {inconsistency_detected && (
                <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-amber-300">{warning_message}</span>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-slate-400 mb-1">Show Rate Δ</div>
                  <DeltaIndicator value={metrics.show_rate_delta} suffix="%" size="large" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Close Rate Δ</div>
                  <DeltaIndicator value={metrics.close_rate_delta} suffix="%" size="large" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Revenue Δ</div>
                  <DeltaIndicator value={metrics.revenue_delta} suffix="" size="large" />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "bg-slate-900/50 border-slate-700/50",
      inconsistency_detected && "border-amber-500/50"
    )} data-testid="impulse-alignment-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-violet-400" />
              Impulse Alignment
            </CardTitle>
            {/* MA-ARCH-136A: Helper text */}
            <p className="text-xs text-slate-500 mt-1">
              {HELPER_DEFINITIONS.impulse_alignment}
            </p>
          </div>
          {hasData && (
            <div className="flex items-center gap-2">
              {metrics.alignment_rate >= 70 ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-400" />
              )}
              <span className={cn(
                "text-sm font-medium",
                metrics.alignment_rate >= 70 ? "text-emerald-400" : "text-amber-400"
              )}>
                {metrics.alignment_rate.toFixed(0)}% Aligned
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {!hasData ? (
          <div className="text-center py-6">
            <Zap className="h-8 w-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No eligible appointments</p>
            <p className="text-xs text-slate-500 mt-1">
              Requires appointments with both script_id and message_variant
            </p>
          </div>
        ) : (
          <>
            {/* MA-ARCH-136A: Enhanced Warning Banner with >30% threshold */}
            {inconsistency_detected && inconsistency_rate > 30 && (
              <WarningBanner
                severity="warning"
                icon={AlertTriangle}
                title="Message Inconsistency Detected"
                message={`${inconsistency_rate.toFixed(0)}% of appointments use scripts that don't match the acquisition message. Scripts should reinforce the message that triggered the lead.`}
              />
            )}
            
            {/* Warning for lower inconsistency rates */}
            {inconsistency_detected && inconsistency_rate <= 30 && (
              <WarningBanner
                severity="info"
                icon={Info}
                message={`${inconsistency_rate.toFixed(0)}% of appointments have misaligned scripts. Monitor for trends.`}
                compact
              />
            )}
            
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  <span className="text-xs text-emerald-400 uppercase">Aligned</span>
                </div>
                <div className="text-2xl font-bold text-white">{metrics.aligned_appointments}</div>
                <div className="text-xs text-slate-400">appointments</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="text-xs text-amber-400 uppercase">Misaligned</span>
                </div>
                <div className="text-2xl font-bold text-white">{metrics.misaligned_appointments}</div>
                <div className="text-xs text-slate-400">appointments</div>
              </div>
            </div>
            
            {/* Performance Comparison */}
            <div className="space-y-3">
              <MetricCard
                label="Show Rate"
                aligned={metrics.aligned_show_rate}
                misaligned={metrics.misaligned_show_rate}
                delta={metrics.show_rate_delta}
                suffix="%"
                higherIsBetter={true}
                icon={Target}
              />
              
              <MetricCard
                label="Close Rate"
                aligned={metrics.aligned_close_rate}
                misaligned={metrics.misaligned_close_rate}
                delta={metrics.close_rate_delta}
                suffix="%"
                higherIsBetter={true}
                icon={CheckCircle2}
              />
              
              <MetricCard
                label="Revenue"
                aligned={metrics.revenue_aligned}
                misaligned={metrics.revenue_misaligned}
                delta={metrics.revenue_delta}
                suffix=""
                higherIsBetter={true}
                icon={DollarSign}
              />
            </div>
            
            {/* Per-Appointment Revenue */}
            <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Revenue per Appointment</div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-emerald-400 text-sm">Aligned: </span>
                  <span className="text-white font-medium">${metrics.revenue_per_aligned_appt.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-amber-400 text-sm">Misaligned: </span>
                  <span className="text-white font-medium">${metrics.revenue_per_misaligned_appt.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
